import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Account } from '../models/account';
import { Observable, BehaviorSubject, tap } from 'rxjs';
import { KYC } from '../models/kyc';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly ROLE_STORAGE_KEY = 'currentUserRole';

  private currentUserSubject: BehaviorSubject<Account | null>;
  public currentUser$: Observable<Account | null>;

  constructor(private http: HttpClient) {
    const storedRole = localStorage.getItem(this.ROLE_STORAGE_KEY);
    this.currentUserSubject = new BehaviorSubject<Account | null>(
      storedRole ? ({ role: storedRole } as Account) : null
    );

    this.currentUser$ = this.currentUserSubject.asObservable();
  }

  login(account: Account): Observable<Account> {
    return this.http.post<Account>(`${environment.apiUrl}/accounts/login/`, account)
      .pipe(
        tap(userResponse => {
          if (userResponse.role) {
            localStorage.setItem(this.ROLE_STORAGE_KEY, userResponse.role);
          }
          this.currentUserSubject.next(userResponse);
        })
      );
  }

  logout(): void {
    localStorage.removeItem(this.ROLE_STORAGE_KEY);
    this.currentUserSubject.next(null);
  }

  getKYCVerifications(userId: number): Observable<KYC[]> {
    return this.http.get<KYC[]>(`${environment.apiUrl}/admin/accounts/${userId}/kyc_verifications/`);
  }
  
  deactivateKYC(userId: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/admin/accounts/${userId}/kyc_verifications/`);
  }

  public get currentUserValue(): Account | null {
    return this.currentUserSubject.value;
  }

  public isStaff(): boolean {
    return this.currentUserValue?.role === 'STAFF';
  }

  public isFinancePartner(): boolean {
    return this.currentUserValue?.role === 'FINANCE_PARTNER';
  }

  public hasFinanceAccess(): boolean {
    return this.isStaff() || this.isFinancePartner();
  }

  public canCreateAdminTransaction(): boolean {
    return this.isStaff();
  }

  public canRunCashOutPaymentActions(): boolean {
    return this.isStaff();
  }

  public canEditCashOutWorkflowStage(): boolean {
    return this.isStaff();
  }

  public canEditAccountTrusted(): boolean {
    return this.isStaff();
  }

  public canEditStaffObservations(): boolean {
    return this.isStaff();
  }
}
